import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { BsArrowLeft, BsDownload, BsLink45Deg, BsMap } from "react-icons/bs";
import Cabecalho from "../../../components/Cabecalho/Cabecalho";
import StatisticsNav from '../../../components/StatisticsNav';
import NatureMultiSelect from "../../../components/NatureMultiSelect";
import { rotuloNatureza } from "../../../Utils/naturezaLabel";
import api from "../../../Utils/api";
import { downloadCsv } from "../../../Utils/csvExport";
import { buildFilterSearch, readFiltersFromLocation } from "../../../Utils/statisticsFilterUrl";
import { readSavedViews, writeSavedViews } from "../../../Utils/statisticsSavedViews";
import "../styles/dashboard-modern.css";
import "./estatisticas.css";

const localToday = new Date();
const today = `${localToday.getFullYear()}-${String(localToday.getMonth() + 1).padStart(2, "0")}-${String(localToday.getDate()).padStart(2, "0")}`;
const startOfYear = `${localToday.getFullYear()}-01-01`;

function defaultFilters() {
  return {
    from: startOfYear,
    to: today,
    municipality: "",
    zone: "",
    localityType: "",
    localityId: "",
    natureIds: [],
    natureId: "",
    compareWithPrevious: false,
  };
}

function previousPeriod(from, to) {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  const days = Math.round((end - start) / 86400000) + 1;
  if (!Number.isFinite(days) || days < 1) return null;
  const previousEnd = new Date(start.getTime() - 86400000);
  const previousStart = new Date(previousEnd.getTime() - (days - 1) * 86400000);
  const iso = (date) => date.toISOString().slice(0, 10);
  return { from: iso(previousStart), to: iso(previousEnd) };
}

const Estatistica = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFilters = useMemo(
    () => readFiltersFromLocation(location, defaultFilters()),
    // Filters are intentionally initialized once; subsequent changes are applied through the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [statistics, setStatistics] = useState({
    total: 0,
    truncated: false,
    reports: [],
    indicators: { total: 0, involvedCount: 0, natures: [], monthly: [], pendingNatureCount: 0 },
    options: { municipalities: [], natures: [] },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [draft, setDraft] = useState(initialFilters);
  const [comparison, setComparison] = useState(null);
  const [comparisonError, setComparisonError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [savedViews, setSavedViews] = useState(() => readSavedViews());
  const [savedViewName, setSavedViewName] = useState("");
  const [selectedSavedView, setSelectedSavedView] = useState("");
  const [linkMessage, setLinkMessage] = useState("");

  const natureOptions = useMemo(() => [
    ...(statistics.options.natures || []).map((nature) => ({
      value: String(nature.id),
      label: `${rotuloNatureza(nature)}${nature.ativo ? "" : " (inativa)"}`,
    })),
    { value: "pendente", label: "Pendentes de classificação" },
  ], [statistics.options.natures]);
  const selectedNatureOptions = useMemo(
    () => natureOptions.filter((option) => draft.natureIds.includes(option.value)),
    [natureOptions, draft.natureIds]
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const params = {
      from: filters.from,
      to: filters.to,
      municipality: filters.municipality || undefined,
      zone: filters.zone || undefined,
      localityType: filters.localityType || undefined,
      localityId: filters.localityId || undefined,
      natureIds: filters.natureIds.length ? filters.natureIds.join(",") : undefined,
    };
    api.get("/incidentReport/statistics", {
      params,
    })
      .then(({ data }) => {
        if (active) setStatistics(data);
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.response?.data?.message || "Não foi possível carregar as estatísticas.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    setComparison(null);
    setComparisonError("");
    if (filters.compareWithPrevious) {
      const period = previousPeriod(filters.from, filters.to);
      if (period) {
        api.get("/incidentReport/statistics", {
          params: { ...params, ...period },
        })
          .then(({ data }) => {
            if (active) setComparison(data);
          })
          .catch(() => {
            if (active) setComparisonError("Não foi possível carregar o período anterior.");
          });
      }
    }
    return () => {
      active = false;
    };
  }, [filters.from, filters.to, filters.municipality, filters.zone, filters.localityType, filters.localityId, filters.natureIds, filters.compareWithPrevious]);

  const monthly = useMemo(
    () => (statistics.indicators.monthly || []).map((item) => {
      const [year, month] = item.mes.split("-").map(Number);
      return {
        month: new Date(year, month - 1, 1).toLocaleDateString("pt-BR", {
          month: "short",
          year: "2-digit",
        }),
        ocorrencias: item.ocorrencias,
      };
    }),
    [statistics.indicators.monthly]
  );

  const averagePerMonth = useMemo(() => {
    const first = new Date(`${filters.from}T12:00:00`);
    const last = new Date(`${filters.to}T12:00:00`);
    const months = Math.max(
      (last.getFullYear() - first.getFullYear()) * 12 +
      last.getMonth() - first.getMonth() + 1,
      1
    );
    return (statistics.indicators.total / months).toFixed(1);
  }, [filters.from, filters.to, statistics.indicators.total]);

  const updateFilter = (field) => (event) =>
    setDraft((current) => ({ ...current, [field]: event.target.value, ...(field === "municipality" || field === "zone" || field === "localityType" ? { localityId: "" } : {}) }));

  const updateNatureSelection = (options) => {
    const natureIds = [...new Set((options || []).map((option) => String(option.value)))];
    setDraft((current) => ({
      ...current,
      natureIds,
      natureId: natureIds.length === 1 ? natureIds[0] : "",
    }));
  };

  const applyFilterValues = (nextFilters) => {
    if (!nextFilters.from || !nextFilters.to || nextFilters.from > nextFilters.to) {
      setValidationError("Informe um intervalo de datas válido.");
      return;
    }
    setValidationError("");
    const normalized = {
      ...nextFilters,
      natureIds: [...new Set((nextFilters.natureIds || []).map(String))],
      natureId: nextFilters.natureIds?.length === 1 ? String(nextFilters.natureIds[0]) : "",
    };
    setDraft(normalized);
    setFilters(normalized);
    const search = buildFilterSearch(normalized);
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, {
      replace: true,
      state: { filters: normalized },
    });
  };

  const applyFilters = (event) => {
    event.preventDefault();
    applyFilterValues(draft);
  };

  const clearFilters = () => {
    const cleared = defaultFilters();
    setDraft(cleared);
    applyFilterValues(cleared);
  };

  const saveCurrentView = () => {
    const name = savedViewName.trim();
    if (!name) return;
    const view = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: name.slice(0, 60),
      filters: { ...draft, natureIds: [...draft.natureIds] },
    };
    const nextViews = [view, ...savedViews].slice(0, 20);
    writeSavedViews(nextViews);
    setSavedViews(nextViews);
    setSelectedSavedView(view.id);
    setSavedViewName("");
  };

  const loadSavedView = () => {
    const view = savedViews.find((item) => item.id === selectedSavedView);
    if (!view) return;
    applyFilterValues({ ...defaultFilters(), ...view.filters });
  };

  const removeSavedView = () => {
    const nextViews = savedViews.filter((item) => item.id !== selectedSavedView);
    writeSavedViews(nextViews);
    setSavedViews(nextViews);
    setSelectedSavedView("");
  };

  const copyFilterLink = async () => {
    const query = buildFilterSearch(filters);
    const url = `${window.location.origin}${location.pathname}${query ? `?${query}` : ""}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkMessage("Link dos filtros copiado.");
    } catch {
      setLinkMessage("Não foi possível copiar automaticamente. Use o endereço da página para compartilhar os filtros.");
    }
  };

  const exportStatistics = () => {
    const reports = (statistics.reports || []).map((report) => [
      report.numero,
      report.data,
      report.horario,
      report.natureza,
      report.bairro,
      report.municipio,
    ]);
    downloadCsv(`bo-online-estatisticas-${filters.from}-${filters.to}.csv`,
      ["Boletim", "Data do fato", "Hora", "Natureza", "Bairro", "Município"], reports);
  };

  const openMap = () => {
    const nextFilters = {
      ...filters,
      natureIds: [...new Set((filters.natureIds || []).map(String))],
      natureId: filters.natureIds?.length === 1 ? String(filters.natureIds[0]) : "",
    };
    const search = buildFilterSearch(nextFilters);
    navigate({
      pathname: "/dashboard/estatisticas/mapa",
      search: search ? `?${search}` : "",
    }, { state: { filters: nextFilters } });
  };

  return (
    <>
      <Cabecalho />
      <main className="statistics-page">
        <Container>
          <StatisticsNav />
          <div className="statistics-heading">
            <div>
              <p className="dashboard-eyebrow">Análise descritiva</p>
              <h2>Estatísticas de ocorrências</h2>
              <p className="dashboard-intro">Indicadores calculados diretamente sobre os registros do banco de dados.</p>
            </div>
            <div className="statistics-heading-actions">
              <Button
                variant="primary"
                onClick={openMap}
              >
                <BsMap className="me-2" /> Ver no mapa
              </Button>
              <Button variant="outline-secondary" onClick={copyFilterLink}>
                <BsLink45Deg className="me-2" /> Copiar link
              </Button>
              <Button variant="outline-secondary" onClick={exportStatistics} disabled={!statistics.reports.length}>
                <BsDownload className="me-2" /> Exportar CSV
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate("/dashboard")}>
                <BsArrowLeft className="me-2" /> Voltar
              </Button>
            </div>
          </div>

          {linkMessage && <Alert variant="info" className="py-2">{linkMessage}</Alert>}
          <Form className="statistics-filters" aria-label="Filtros das estatísticas" onSubmit={applyFilters}>
            <Form.Group controlId="statistics-from">
              <Form.Label>Data inicial</Form.Label>
              <Form.Control
                type="date"
                value={draft.from}
                max={draft.to}
                onChange={updateFilter("from")}
              />
            </Form.Group>
            <Form.Group controlId="statistics-to">
              <Form.Label>Data final</Form.Label>
              <Form.Control
                type="date"
                value={draft.to}
                min={draft.from}
                max={today}
                onChange={updateFilter("to")}
              />
            </Form.Group>
            <Form.Group controlId="statistics-municipality">
              <Form.Label>Município</Form.Label>
              <Form.Select value={draft.municipality} onChange={updateFilter("municipality")}>
                <option value="">Todos</option>
                {statistics.options.municipalities.map((municipality) => (
                  <option key={municipality} value={municipality}>{municipality}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="statistics-zone"><Form.Label>Zona</Form.Label><Form.Select value={draft.zone || ""} onChange={updateFilter("zone")}><option value="">Todas</option><option value="URBANA">Urbana</option><option value="RURAL">Rural</option><option value="NAO_INFORMADA">Não informada</option></Form.Select></Form.Group>
            <Form.Group controlId="statistics-locality-type"><Form.Label>Tipo territorial</Form.Label><Form.Select value={draft.localityType || ""} onChange={updateFilter("localityType")}><option value="">Todos</option>{["BAIRRO", "POVOADO", "LOCALIDADE_RURAL", "VILA", "DISTRITO", "ASSENTAMENTO", "COMUNIDADE", "OUTRO"].map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}</Form.Select></Form.Group>
            <Form.Group controlId="statistics-locality"><Form.Label>Localidade</Form.Label><Form.Select value={draft.localityId || ""} onChange={updateFilter("localityId")}><option value="">Todas</option>{(statistics.options.localities || []).map((item) => <option key={item.id} value={item.id}>{item.nome} · {item.municipio}</option>)}</Form.Select></Form.Group>
            <Form.Group controlId="statistics-nature">
              <Form.Label>Natureza</Form.Label>
              <NatureMultiSelect
                inputId="statistics-nature"
                ariaLabel="Natureza"
                value={selectedNatureOptions}
                options={natureOptions}
                onChange={updateNatureSelection}
              />
            </Form.Group>
            <Form.Group className="statistics-compare-option" controlId="statistics-compare">
              <Form.Check
                type="checkbox"
                label="Comparar com o período anterior de mesma duração"
                checked={Boolean(draft.compareWithPrevious)}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  compareWithPrevious: event.target.checked,
                }))}
              />
            </Form.Group>
            <div className="statistics-filter-actions">
              <Button type="submit">Aplicar filtros</Button>
              <Button type="button" variant="outline-secondary" onClick={clearFilters}>Limpar</Button>
            </div>
          </Form>

          <section className="statistics-saved-views" aria-label="Filtros salvos neste navegador">
            <Form.Select
              aria-label="Filtros salvos"
              value={selectedSavedView}
              onChange={(event) => setSelectedSavedView(event.target.value)}
            >
              <option value="">Escolha um filtro salvo</option>
              {savedViews.map((view) => <option key={view.id} value={view.id}>{view.name}</option>)}
            </Form.Select>
            <Button type="button" variant="outline-secondary" onClick={loadSavedView} disabled={!selectedSavedView}>
              Carregar
            </Button>
            <Button type="button" variant="outline-secondary" onClick={removeSavedView} disabled={!selectedSavedView}>
              Remover
            </Button>
            <Form.Control
              aria-label="Nome do filtro salvo"
              value={savedViewName}
              maxLength={60}
              placeholder="Nome para salvar os filtros atuais"
              onChange={(event) => setSavedViewName(event.target.value)}
            />
            <Button type="button" variant="outline-primary" onClick={saveCurrentView} disabled={!savedViewName.trim()}>
              Salvar filtros
            </Button>
          </section>

          {error && <Alert variant="danger">{error}</Alert>}
          {validationError && <Alert variant="warning" role="alert">{validationError}</Alert>}
          {statistics.truncated && (
            <Alert variant="info" className="mt-3">
              Os indicadores usam todos os registros, mas a lista exportável está limitada aos 10.000 mais recentes.
            </Alert>
          )}
          {loading ? (
            <div className="statistics-loading"><Spinner animation="border" role="status" /><span>Consultando o banco de dados...</span></div>
          ) : !error && (
            <>
              <Row className="g-3 statistics-indicators">
                <Col xs={6} lg={3}><article className="stat-card"><span>Ocorrências</span><strong>{statistics.indicators.total}</strong></article></Col>
                <Col xs={6} lg={3}><article className="stat-card"><span>Naturezas oficiais</span><strong>{statistics.indicators.natures.filter((item) => item.naturezaId).length}</strong></article></Col>
                <Col xs={6} lg={3}><article className="stat-card"><span>Envolvidos registrados</span><strong>{statistics.indicators.involvedCount}</strong></article></Col>
                <Col xs={6} lg={3}><article className="stat-card"><span>Média mensal</span><strong>{averagePerMonth}</strong></article></Col>
              </Row>

              {filters.compareWithPrevious && (
                <section className="statistics-comparison" aria-label="Comparação com o período anterior">
                  <div>
                    <span>Período anterior</span>
                    <strong>{comparison ? comparison.indicators.total : "—"}</strong>
                  </div>
                  <div>
                    <span>Variação no total</span>
                    <strong>{comparison
                      ? `${statistics.indicators.total - comparison.indicators.total > 0 ? "+" : ""}${statistics.indicators.total - comparison.indicators.total} ocorrências`
                      : "Calculando..."}</strong>
                  </div>
                  {comparisonError && <span className="statistics-comparison-error">{comparisonError}</span>}
                </section>
              )}

              {statistics.indicators.pendingNatureCount > 0 && (
                <Alert variant="warning" className="mt-3">
                  {statistics.indicators.pendingNatureCount} ocorrência(s) ainda não possuem associação ao catálogo oficial e aparecem como pendentes nos gráficos e relatórios.
                </Alert>
              )}

              <Row className="g-3 statistics-charts">
                <Col xs={12} xl={7}>
                  <section className="chart-card">
                    <h3>Ocorrências por mês</h3>
                    <div className="chart-area">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthly} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                          <XAxis dataKey="month" tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
                          <YAxis allowDecimals={false} tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="ocorrencias" name="Ocorrências" fill="#2585d8" radius={[5, 5, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </section>
                </Col>
                <Col xs={12} xl={5}>
                  <section className="chart-card">
                    <h3>Principais naturezas</h3>
                    <div className="chart-area">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={statistics.indicators.natures.slice(0, 8)} layout="vertical" margin={{ top: 8, right: 12, left: 12, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                          <XAxis type="number" allowDecimals={false} tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
                          <YAxis type="category" dataKey="natureza" width={100} tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="total" name="Ocorrências" fill="#21a179" radius={[0, 5, 5, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </section>
                </Col>
              </Row>
              <section className="statistics-table-card">
                <h3>Distribuição por natureza</h3>
                <Table responsive className="statistics-table mb-0">
                  <thead>
                    <tr><th>Natureza</th><th>Ocorrências</th><th>Participação</th></tr>
                  </thead>
                  <tbody>
                    {statistics.indicators.natures.map((item) => (
                      <tr key={item.natureza}>
                        <td>{item.natureza}</td>
                        <td>{item.total}</td>
                        <td>{statistics.indicators.total
                          ? `${((item.total / statistics.indicators.total) * 100).toFixed(1)}%`
                          : "0%"}</td>
                      </tr>
                    ))}
                    {!statistics.indicators.natures.length && (
                      <tr><td colSpan="3" className="text-center">Sem dados para os filtros selecionados.</td></tr>
                    )}
                  </tbody>
                </Table>
              </section>
            </>
          )}
        </Container>
      </main>
    </>
  );
};

export default Estatistica;
